import { useEffect, useState, useRef } from "react";
import FilePicker from "@/components/FilePicker";
import MessageFiles from "@/components/MessageFiles";
import { api } from "@/lib/api";
import { getCached, setCached } from "@/lib/cache";
import PageLoader from "@/components/PageLoader";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useLocation, useNavigate } from "react-router-dom";
import ProgressRing from "@/components/ProgressRing";
import StatusBadge from "@/components/StatusBadge";
import PaymentCalendar from "@/components/PaymentCalendar";
import Tabs from "@/components/Tabs";
import PasswordInput from "@/components/PasswordInput";
import MarketingSection from "@/components/MarketingSection";
import AccordionCard, { ExpandAllToggle } from "@/components/AccordionCard";
import NotificationBell from "@/components/NotificationBell";
import NotificationsPanel from "@/components/ClientNotifications";
import SubmitTaskPanel from "@/components/SubmitTaskPanel";
import FilterPills from "@/components/FilterPills";
import RequiredActionsPanel from "@/components/RequiredActionsPanel";
import ProjectStatusBox from "@/components/ProjectStatusBox";
import SystemMessagesList from "@/components/SystemMessagesList";
import MessageSearch from "@/components/MessageSearch";
import { splitMessages, sampleFilesOf } from "@/lib/messages";
import useDraft from "@/hooks/useDraft";
import TaskNotes from "@/components/TaskNotes";
import SampleGallery from "@/components/SampleGallery";
import { LinkifiedText, FormatBar } from "@/components/LinkifiedText";
import {
  ArrowSquareOut, ShieldCheck, SignOut,
  DownloadSimple, CurrencyDollar, Sun, Moon, Sparkle,
  PaperPlaneTilt, Copy, Check, PencilSimple, Lifebuoy, CaretDown, CaretUp,
  CheckCircle, Circle,
  PushPin,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

// Reusable due-date badge for samples
function SampleDueBadge({ dueDate }) {
  if (!dueDate) return null;
  const iso = String(dueDate).slice(0, 10);
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return null;
  const due = new Date(y, m - 1, d);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const days = Math.round((due - today) / 86400000);
  let bg = "#dbeafe", color = "#1e3a8a";
  let label = `Due ${due.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
  if (days < 0) { bg = "#fee2e2"; color = "#991b1b"; label += ` · Overdue ${-days}d`; }
  else if (days === 0) { bg = "#fef3c7"; color = "#78350f"; label += " · Today"; }
  else if (days <= 3) { bg = "#fef3c7"; color = "#78350f"; label += ` · in ${days}d`; }
  else { label += ` · in ${days}d`; }
  return (
    <span className="label px-2 py-0.5"
      style={{ background: bg, color, border: `1px solid ${bg}`, fontWeight: 700 }}>
      {label}
    </span>
  );
}

function TaskCard({ task, idx, onVerify, verifying, forceOpen }) {
  const isLink = ["link", "jotform", "acuity", "upload", "custom"].includes(task.kind) && !!task.url;
  const dueBadge = (() => {
    if (!task.due_date) return null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const d = new Date(task.due_date);
    const days = Math.floor((d - today) / 86400000);
    let bg = "#e5e7eb", color = "#374151";
    let label = new Date(task.due_date).toLocaleDateString(undefined, { month: "short", day: "numeric" });
    if (task.status !== "complete" && !task.archived) {
      if (days < 0) { bg = "#fee2e2"; color = "#991b1b"; label = `Overdue ${-days}d`; }
      else if (days <= 2) { bg = "#fef3c7"; color = "#78350f"; label = days === 0 ? "Due today" : `Due in ${days}d`; }
    }
    return <span className="label px-2 py-0.5" style={{ background: bg, color, border: `1px solid ${bg}` }}>{label}</span>;
  })();
  const header = (
    <div>
      <div className="flex flex-wrap items-center gap-3">
        <span className="task-num" style={{ fontSize: "1rem" }}>{String(idx + 1).padStart(2, "0")}</span>
        <StatusBadge status={task.status} testid={`task-status-${task.task_id}`} />
        <h3 className="title-xl text-lg md:text-xl" style={{ fontWeight: 700 }}>{task.title}</h3>
        {dueBadge}
      </div>
    </div>
  );
  return (
    <AccordionCard testid={`task-card-${task.task_id}`} header={header} forceOpen={forceOpen}
      anchorId={`task-${task.task_id}`}>
      <p className="leading-relaxed" style={{ color: "var(--text-dim)" }}>{task.description}</p>
      {(task.files || []).length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {task.files.map(f => (
            <button key={f.file_id} type="button"
              onClick={async () => {
                try {
                  const res = await api.get(`/files/${f.file_id}/download`, { responseType: "blob" });
                  const url = URL.createObjectURL(res.data);
                  const a = document.createElement("a"); a.href = url; a.download = f.filename || "file"; a.click();
                  URL.revokeObjectURL(url);
                } catch (_e) { toast.error("Download failed"); }
              }}
              className="btn-ghost text-xs"
              data-testid={`task-file-${f.file_id}`}>
              <DownloadSimple size={11} weight="bold" /> {f.filename}
            </button>
          ))}
        </div>
      )}
      <div className="mt-5 flex flex-wrap gap-3">
        {isLink && task.url && (
          <a data-testid={`task-open-${task.task_id}`} href={task.url} target="_blank" rel="noreferrer" className="btn-primary">
            Open <ArrowSquareOut size={14} weight="bold" />
          </a>
        )}
        <button data-testid={`task-verify-${task.task_id}`} disabled={verifying} onClick={() => onVerify(task.task_id)} className="btn-ghost">
          <ShieldCheck size={14} weight="bold" /> {verifying ? "Checking…" : "Mark As Complete"}
        </button>
      </div>
      <TaskNotes taskId={task.task_id} anchorId={`task-${task.task_id}`} uploadPath="/me/files"
        initialNotes={task.notes || []} testid="client-task-notes" />
    </AccordionCard>
  );
}

function ChecklistSection({ title, tasks, onVerify, busy }) {
  const [allOpen, setAllOpen] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all"); // "all" | "pending" | "complete" | "archived"
  const live = tasks.filter(t => !t.archived);
  const filtered = statusFilter === "archived" ? tasks.filter(t => t.archived) : live.filter(t => {
    if (statusFilter === "complete") return t.status === "complete";
    if (statusFilter === "pending") return t.status !== "complete";
    return true;
  });
  const counts = {
    all: live.length,
    pending: live.filter(t => t.status !== "complete").length,
    complete: live.filter(t => t.status === "complete").length,
    archived: tasks.length - live.length,
  };
  return (
    <section className="space-y-4" data-testid="checklist-list">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <h2 className="title-xl text-3xl">{title}</h2>
        <div className="flex items-center gap-3">
          {tasks.length > 1 && (
            <ExpandAllToggle testid={`tasks-expand-all`}
              allOpen={allOpen === true}
              onToggle={(next) => setAllOpen(next)} />
          )}
          <p className="label">{filtered.length} / {statusFilter === "archived" ? counts.archived : live.length}</p>
        </div>
      </div>
      {tasks.length > 0 && (
        <FilterPills
          testid="tasks-filter"
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { k: "all", label: `All (${counts.all})` },
            { k: "pending", label: `Pending (${counts.pending})` },
            { k: "complete", label: `Complete (${counts.complete})` },
            ...(counts.archived ? [{ k: "archived", label: `Archived (${counts.archived})` }] : []),
          ]}
        />
      )}
      {filtered.length === 0
        ? <div className="panel p-8 text-center label">No steps in this view</div>
        : filtered.map((t, i) => (
          <TaskCard key={t.task_id} task={t} idx={i}
            onVerify={onVerify} verifying={busy === t.task_id}
            forceOpen={allOpen === null ? undefined : allOpen} />
        ))}
    </section>
  );
}

// ---- Admin-assigned tasks (rendered inside client Tasks tab) ----
function AssignedByTeamCard({ task, onChanged }) {
  const done = task.status === "complete";
  const setStatus = async (s) => {
    try { await api.patch(`/me/admin-tasks/${task.task_id}`, { status: s }); onChanged(); }
    catch (_e) { toast.error("Failed"); }
  };
  const toggleSub = async (s) => {
    try {
      await api.patch(`/me/admin-tasks/${task.task_id}/subtasks/${s.subtask_id}`,
        { title: s.title, done: !s.done });
      onChanged();
    } catch (_e) { toast.error("Failed"); }
  };
  const uploadSubFile = async (s, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    try {
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        await api.post(`/admin-tasks/${task.task_id}/subtasks/${s.subtask_id}/files`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success("Uploaded");
      onChanged && onChanged();
    } catch (_e) { toast.error("Upload failed"); }
  };
  const dlFile = async (f) => {
    try {
      const res = await api.get(`/files/${f.file_id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = f.filename || "file"; a.click();
      URL.revokeObjectURL(url);
    } catch (_e) { toast.error("Download failed"); }
  };
  const dueBadge = (() => {
    if (!task.due_date) return null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const d = new Date(task.due_date);
    const days = Math.floor((d - today) / 86400000);
    let bg = "#e5e7eb", color = "#374151", label = new Date(task.due_date).toLocaleDateString(undefined, { month: "short", day: "numeric" });
    if (task.status !== "complete" && !task.archived) {
      if (days < 0) { bg = "#fee2e2"; color = "#991b1b"; label = `Overdue ${-days}d`; }
      else if (days <= 2) { bg = "#fef3c7"; color = "#78350f"; label = days === 0 ? "Due today" : `Due in ${days}d`; }
    }
    return <span className="label px-2 py-0.5" style={{ background: bg, color, border: `1px solid ${bg}` }}>{label}</span>;
  })();
  const header = (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="label px-2 py-0.5 border" style={{ borderColor: "var(--hair)", fontWeight: 700 }}>
          {task.status.replace("_", " ")}
        </span>
        <span className="label" style={{ color: "var(--text-dim)" }}>Team</span>
        <h4 className="title-xl text-lg" style={{ fontWeight: 700 }}>{task.title}</h4>
        {dueBadge}
      </div>
      {(task.subtasks || []).length > 0 && (
        <p className="label mt-1">{task.subtasks.filter(s => s.done).length}/{task.subtasks.length} subtasks</p>
      )}
    </div>
  );
  return (
    <AccordionCard testid={`assigned-task-${task.task_id}`} header={header}>
      {task.description && (
        <p className="whitespace-pre-wrap text-sm" style={{ color: "var(--text-dim)" }}>{task.description}</p>
      )}
      {(task.subtasks || []).length > 0 && (
        <div className="mt-3 space-y-2">
          {task.subtasks.map(s => (
            <div key={s.subtask_id} className="hair p-2"
              data-testid={`assigned-sub-${s.subtask_id}`}>
              <div className="flex items-start gap-2 text-sm">
                <button type="button" onClick={() => toggleSub(s)}
                  className="btn-ghost text-xs p-1 mt-0.5">
                  {s.done ? <CheckCircle size={16} weight="fill" /> : <Circle size={16} weight="bold" />}
                </button>
                <div className="flex-1">
                  <span style={{ textDecoration: s.done ? "line-through" : "none",
                                 color: s.done ? "var(--text-dim)" : "inherit" }}>{s.title}</span>
                  {s.due_date && (
                    <span className="ml-2 label px-1.5 py-0.5"
                      style={{ background: "var(--panel)", border: "1px solid var(--hair)", color: "var(--text-dim)" }}>
                      Due {new Date(s.due_date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                    </span>
                  )}
                  {s.notes && <p className="text-xs mt-0.5" style={{ color: "var(--text-dim)" }}>{s.notes}</p>}
                  {(s.files || []).length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {s.files.map(f => (
                        <button key={f.file_id} type="button"
                          onClick={() => dlFile(f)}
                          className="btn-ghost text-xs"
                          data-testid={`assigned-sub-file-${f.file_id}`}>
                          <DownloadSimple size={10} weight="bold" /> {f.filename}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <label className="btn-ghost text-xs p-1 cursor-pointer" title="Attach files"
                  data-testid={`assigned-sub-upload-label-${s.subtask_id}`}>
                  <PaperPlaneTilt size={10} weight="bold" />
                  <input type="file" multiple className="hidden"
                    data-testid={`assigned-sub-upload-${s.subtask_id}`}
                    onChange={(e) => { uploadSubFile(s, e.target.files); e.target.value = ""; }} />
                </label>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-4">
        <button data-testid={`assigned-mark-${task.task_id}`}
          onClick={() => setStatus(done ? "todo" : "complete")}
          className={done ? "btn-ghost text-xs" : "btn-primary text-xs"}>
          <ShieldCheck size={12} weight="bold" /> {done ? "Mark as todo" : "Mark complete"}
        </button>
      </div>
    </AccordionCard>
  );
}

function AssignedByTeamSection({ tasks, onChanged }) {
  if (!tasks || tasks.length === 0) return null;
  return (
    <section className="space-y-3" data-testid="assigned-by-team-section">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <h2 className="title-xl text-2xl">Assigned by your team</h2>
        <p className="label">{tasks.length} item{tasks.length === 1 ? "" : "s"}</p>
      </div>
      {tasks.map(t => (
        <AssignedByTeamCard key={t.task_id} task={t} onChanged={onChanged} />
      ))}
    </section>
  );
}

function CopyableField({ label, value, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value); setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div>
      <p className="label mb-1">{label}</p>
      <div className="flex items-center gap-2">
        <div className="hair px-3 py-2 flex-1 font-mono text-sm truncate">{value}</div>
        <button data-testid={testid} onClick={copy} className="btn-ghost text-xs">
          {copied ? <><Check size={12} weight="bold" /> Copied</> : <><Copy size={12} weight="bold" /> Copy</>}
        </button>
      </div>
    </div>
  );
}

function PaymentsPanel({ payments, methods }) {
  const total = payments.reduce((a, p) => a + Number(p.amount), 0);
  const paid = payments.filter(p => p.status === "paid").reduce((a, p) => a + Number(p.amount), 0);
  const nextUnpaid = payments.find(p => p.status !== "paid");
  return (
    <div className="space-y-6" data-testid="payments-panel">
      {nextUnpaid && (
        <div className="panel p-6" data-testid="next-payment-card"
             style={{ background: "var(--invert-bg)", color: "var(--invert-text)" }}>
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <div>
              <p className="label" style={{ color: "var(--invert-text)", opacity: 0.6 }}>Next payment due</p>
              <p className="title-xl text-5xl mt-2">${Number(nextUnpaid.amount).toFixed(2)}</p>
              <p className="label mt-1" style={{ color: "var(--invert-text)", opacity: 0.7 }}>Due {nextUnpaid.due_date}{nextUnpaid.note ? ` · ${nextUnpaid.note}` : ""}</p>
            </div>
            {methods?.paypal_link && (
              <a href={methods.paypal_link} target="_blank" rel="noreferrer"
                 data-testid="pay-next-btn"
                 className="inline-flex items-center gap-2 px-6 py-4"
                 style={{ background: "var(--bg)", color: "var(--text)", border: "1px solid var(--bg)" }}>
                <CurrencyDollar size={16} weight="bold" /> Pay ${Number(nextUnpaid.amount).toFixed(2)} via PayPal
              </a>
            )}
          </div>
          <p className="text-xs mt-4" style={{ color: "var(--invert-text)", opacity: 0.6 }}>
            Note: enter ${Number(nextUnpaid.amount).toFixed(2)} on the PayPal page. Or use Zelle / Cash App below.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="panel p-6">
          <p className="label">Total</p>
          <p className="title-xl text-4xl mt-2">${total.toFixed(2)}</p>
        </div>
        <div className="panel p-6">
          <p className="label">Paid</p>
          <p className="title-xl text-4xl mt-2">${paid.toFixed(2)}</p>
        </div>
        <div className="panel p-6">
          <p className="label">Remaining</p>
          <p className="title-xl text-4xl mt-2">${(total - paid).toFixed(2)}</p>
        </div>
      </div>

      <div className="panel p-6">
        <h3 className="title-xl text-2xl mb-1">Pay a scheduled invoice</h3>
        <p className="text-sm mb-6" style={{ color: "var(--text-dim)" }}>Choose any of the methods below. All go to PrettyGrlsDesign.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {methods?.paypal_link && (
            <a href={methods.paypal_link} target="_blank" rel="noreferrer"
               className="panel p-5 card-lift" data-testid="pay-paypal">
              <p className="label">Method 01</p>
              <p className="title-xl text-xl mt-2">Credit / Debit / PayPal</p>
              <p className="text-sm mt-2" style={{ color: "var(--text-dim)" }}>Opens our secure PayPal checkout page.</p>
              <span className="btn-primary mt-4 w-full justify-center"><CurrencyDollar size={14} weight="bold" /> Pay now</span>
            </a>
          )}
          {methods?.zelle_email && (
            <div className="panel p-5" data-testid="pay-zelle">
              <p className="label">Method 02</p>
              <p className="title-xl text-xl mt-2">Zelle</p>
              <p className="text-sm mt-2 mb-4" style={{ color: "var(--text-dim)" }}>Please send funds to the email below using the recipient name.</p>
              <div className="space-y-2">
                <CopyableField label="Email" value={methods.zelle_email} testid="copy-zelle-email" />
                <CopyableField label="Name" value={methods.zelle_name} testid="copy-zelle-name" />
              </div>
            </div>
          )}
          {methods?.cashapp_url && (
            <a href={methods.cashapp_url} target="_blank" rel="noreferrer"
               className="panel p-5 card-lift" data-testid="pay-cashapp">
              <p className="label">Method 03</p>
              <p className="title-xl text-xl mt-2">Cash App</p>
              <p className="text-sm mt-2" style={{ color: "var(--text-dim)" }}>Opens our Cash App tag in a new tab.</p>
              <span className="btn-primary mt-4 w-full justify-center"><ArrowSquareOut size={14} weight="bold" /> Open</span>
            </a>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3"><PaymentCalendar payments={payments} /></div>
        <div className="lg:col-span-2 panel p-6">
          <h3 className="title-xl text-2xl mb-3">Scheduled</h3>
          {payments.filter(p => !p.archived).length === 0
            ? <p className="text-sm" style={{ color: "var(--text-dim)" }}>No open payments.</p>
            : (
              <div className="space-y-3">
                {payments.filter(p => !p.archived).map((p) => <ClientPaymentRow key={p.payment_id} p={p} />)}
              </div>
            )}
          {payments.some(p => p.archived) && (
            <div className="mt-4">
              <AccordionCard testid="client-archived-payments"
                header={<p className="font-semibold">Archived ({payments.filter(p => p.archived).length})</p>}>
                <div className="space-y-3 mt-2">
                  {payments.filter(p => p.archived).map((p) => <ClientPaymentRow key={p.payment_id} p={p} />)}
                </div>
              </AccordionCard>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ClientPaymentRow({ p }) {
  return (
                  <div className="hair p-4" data-testid={`payment-${p.payment_id}`}>
                    <div className="flex items-center justify-between">
                      <p className="title-xl text-xl">${Number(p.amount).toFixed(2)}</p>
                      <span className="label px-2 py-1 border"
                            style={p.status === "paid"
                              ? { background: "var(--invert-bg)", color: "var(--invert-text)", borderColor: "var(--invert-bg)" }
                              : { borderColor: "var(--text)" }}>
                        {p.status}
                      </span>
                    </div>
                    <p className="label mt-2">Due {p.due_date}</p>
                    {p.status === "paid" && p.paid_at && (
                      <p className="label mt-1" style={{ color: "var(--text-dim)" }}>
                        Paid {new Date(p.paid_at).toLocaleDateString()}
                      </p>
                    )}
                    {p.note && <p className="text-sm mt-2 whitespace-pre-line" style={{ color: "var(--text-dim)" }}>{p.note}</p>}
                  </div>
  );
}

function FilesPanel({ files }) {
  const all = files;
  const dl = async (f) => {
    try {
      const res = await api.get(`/files/${f.file_id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = f.original_filename; a.click();
      URL.revokeObjectURL(url);
    } catch (_e) { toast.error("Download failed"); }
  };
  const jotforms = all.filter(f => f.tag === "jotform");
  const samples = all.filter(f => f.tag === "sample");
  const taskAttach = all.filter(f => f.tag === "task_attachment");
  const msgAttach = all.filter(f => f.tag === "message");
  const others = all.filter(f => !["jotform","sample","task_attachment","message"].includes(f.tag));
  const section = (title, arr, badge) => arr.length > 0 && (
    <div className="mb-6">
      <p className="label mb-3">{title} · {arr.length}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {arr.map((f) => (
          <div key={f.file_id} className="panel p-5" data-testid={`file-${f.file_id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                {badge && <span className="sample-badge">{badge}</span>}
                <p className="title-xl text-lg truncate mt-1">{f.original_filename}</p>
                <p className="label mt-1">{(f.size/1024).toFixed(1)} KB · {new Date(f.uploaded_at).toLocaleDateString()}</p>
                {f.note && <p className="text-sm mt-2" style={{ color: "var(--text-dim)" }}>{f.note}</p>}
              </div>
              <button data-testid={`download-${f.file_id}`} onClick={() => dl(f)} className="btn-ghost">
                <DownloadSimple size={14} weight="bold" /> Download
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
  return (
    <div data-testid="files-panel">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="title-xl text-3xl">Your files</h2>
        <p className="label">{all.length} total</p>
      </div>
      {all.length === 0
        ? <div className="panel p-6 text-center"><p style={{ color: "var(--text-dim)" }}>No files have been shared with you yet.</p></div>
        : (<>
            {section("Completed Jotforms", jotforms, "Jotform")}
            {section("Samples", samples, "Sample")}
            {section("Task attachments", taskAttach, "Task")}
            {section("Message attachments", msgAttach, "Message")}
            {section("Other files", others)}
          </>)}
    </div>
  );
}

function SamplesPanel({ messages, files, clientType, webDesignEditUrl, onSubmitEdits, onDecide, onNotifyJotform }) {
  const samples = messages.filter(m => m.sample);
  const editsById = messages.filter(m => m.sample_edits).reduce((acc, m) => {
    (acc[m.reply_to_message_id] = acc[m.reply_to_message_id] || []).push(m); return acc;
  }, {});
  const [allOpen, setAllOpen] = useState(null);
  return (
    <div data-testid="samples-panel">
      <div className="flex items-baseline justify-between gap-3 mb-4 flex-wrap">
        <h2 className="title-xl text-3xl">Samples</h2>
        <div className="flex items-center gap-3">
          {samples.length > 1 && (
            <ExpandAllToggle testid="samples-expand-all"
              allOpen={allOpen === true}
              onToggle={(next) => setAllOpen(next)} />
          )}
          <p className="label">{samples.length} · you are {clientType}</p>
        </div>
      </div>
      {samples.length === 0
        ? <div className="panel p-10 text-center"><p style={{ color: "var(--text-dim)" }}>No samples yet — your team will send previews here.</p></div>
        : (
          <div className="space-y-6">
            {samples.map((s) => (
              <SampleItem key={s.message_id} sample={s} files={files}
                edits={editsById[s.message_id] || []} clientType={clientType}
                webDesignEditUrl={webDesignEditUrl} onSubmitEdits={onSubmitEdits}
                onDecide={onDecide} onNotifyJotform={onNotifyJotform}
                forceOpen={allOpen === null ? undefined : allOpen} />
            ))}
          </div>
        )}
    </div>
  );
}

function SampleStatusBadge({ status }) {
  const s = status || "pending";
  const map = {
    pending:         { bg: "#fef3c7", color: "#78350f", label: "Awaiting your decision" },
    approved:        { bg: "#d1fae5", color: "#065f46", label: "Project approved" },
    declined:        { bg: "#fee2e2", color: "#991b1b", label: "Declined" },
    edits_requested: { bg: "#dbeafe", color: "#1e3a8a", label: "Pending update" },
  };
  const t = map[s] || map.pending;
  return <span className="label px-2 py-0.5" style={{ background: t.bg, color: t.color, border: `1px solid ${t.bg}` }}>{t.label}</span>;
}

function SampleItem({ sample, files, edits, clientType, webDesignEditUrl, onSubmitEdits, onDecide, onNotifyJotform, forceOpen }) {
  const [editText, setEditText] = useState("");
  const editFilesRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [commentOpen, setCommentOpen] = useState(false);
  const [pwCopied, setPwCopied] = useState(false);
  const status = sample.sample_status || "pending";
  const allowEdits = sample.allow_edits !== false; // default true if unset
  const editsChannel = sample.edits_channel === "jotform" ? "jotform"
    : sample.edits_channel === "none" ? "none"
    : "comment";
  const sampleFiles = sampleFilesOf(sample, files);
  const copyPw = async () => {
    try {
      await navigator.clipboard.writeText(sample.preview_password || "");
      setPwCopied(true); setTimeout(() => setPwCopied(false), 1200);
    } catch { toast.error("Copy failed"); }
  };
  const dl = async (f) => {
    const res = await api.get(`/files/${f.file_id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = f.original_filename; a.click();
    URL.revokeObjectURL(url);
  };
  const submit = async () => {
    if (!editText.trim()) return;
    setBusy(true);
    try {
      const file_ids = [];
      for (const f of Array.from(editFilesRef.current?.files || [])) {
        const fd = new FormData(); fd.append("file", f); fd.append("note", `Sample edits: ${sample.sample_title || ""}`);
        const { data } = await api.post("/me/files", fd, { headers: { "Content-Type": "multipart/form-data" } });
        file_ids.push(data.file_id);
      }
      await onSubmitEdits(sample.message_id, editText, file_ids);
      toast.success("Edits sent — sample declined");
      setEditText(""); setCommentOpen(false);
      if (editFilesRef.current) editFilesRef.current.value = "";
    } catch (_e) { toast.error("Failed"); }
    finally { setBusy(false); }
  };
  const decide = async (decision, note) => {
    setBusy(true);
    try {
      await onDecide(sample.message_id, decision, note || "");
      toast.success(decision === "approved" ? "Project approved" : "Sample declined");
    } catch (_e) { toast.error("Failed"); }
    finally { setBusy(false); }
  };
  const openJotform = async () => {
    window.open(webDesignEditUrl, "_blank", "noopener,noreferrer");
    try {
      await onNotifyJotform(sample.message_id);
      toast.success("Sample declined — your edits are on their way");
    } catch (_e) { toast.error("Couldn't update sample status"); }
  };
  const header = (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="sample-badge"><Sparkle size={10} weight="fill" /> Sample</span>
        <SampleStatusBadge status={status} />
        {status === "pending" && <SampleDueBadge dueDate={sample.due_date} />}
        <p className="title-xl text-lg" style={{ fontWeight: 700 }}>{sample.subject.replace(/^\[Sample\]\s*/, "")}</p>
      </div>
      <p className="label mt-1">{new Date(sample.sent_at).toLocaleString()}{edits.length > 0 && ` · ${edits.length} edit${edits.length === 1 ? "" : "s"}`}</p>
    </div>
  );
  return (
    <AccordionCard testid={`sample-${sample.message_id}`} header={header} forceOpen={forceOpen}
      anchorId={`sample-${sample.message_id}`}>
      <p className="whitespace-pre-line leading-relaxed" style={{ color: "var(--text-dim)" }}>
        {(sample.body || "").replace(/\n*Link:\s*https?:\/\/\S+\s*$/, "").trim()}
      </p>
      <div className="flex flex-wrap gap-3 mt-4">
        {sample.sample_url && (
          <a href={sample.sample_url} target="_blank" rel="noreferrer" className="btn-primary text-xs">
            <ArrowSquareOut size={12} weight="bold" /> View sample
          </a>
        )}
        {sampleFiles.map((f) => (
          <button key={f.file_id} onClick={() => dl(f)} className="btn-primary text-xs"
            data-testid={`sample-download-${f.file_id}`} title={f.original_filename}>
            <DownloadSimple size={12} weight="bold" />
            {sampleFiles.length > 1 ? <span className="truncate max-w-[180px]">{f.original_filename}</span> : "Download sample"}
          </button>
        ))}
      </div>
      <SampleGallery files={sampleFiles} onDownload={dl} testid={`client-sample-gallery-${sample.message_id}`} />
      {sample.preview_password && (
        <div className="mt-3 panel p-3"
          data-testid={`sample-preview-password-${sample.message_id}`}
          style={{ borderStyle: "dashed" }}>
          <p className="label mb-1" style={{ color: "var(--text-dim)" }}>
            Preview password (enter this on the preview site)
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <code className="px-2 py-1 text-sm"
              style={{ background: "var(--bg-alt, rgba(255,255,255,0.06))",
                       border: "1px solid var(--hair)", borderRadius: 4,
                       fontFamily: "monospace" }}>
              {sample.preview_password}
            </code>
            <button type="button" onClick={copyPw}
              data-testid={`sample-preview-password-copy-${sample.message_id}`}
              className="btn-ghost text-xs">
              {pwCopied ? "Copied!" : "Copy"}
            </button>
          </div>
        </div>
      )}
      {/* Decision + edits row */}
      {status === "pending" ? (
        <div className="flex flex-wrap gap-3 mt-4">
          <button data-testid={`sample-approve-${sample.message_id}`}
            disabled={busy} onClick={() => decide("approved")}
            className="btn-primary text-xs"
            style={{ background: "#059669", color: "#fff", borderColor: "#059669" }}>
            <CheckCircle size={12} weight="bold" /> Approve project
          </button>
          {allowEdits && (
            editsChannel === "jotform" ? (
              <button data-testid={`edit-jotform-${sample.message_id}`}
                disabled={busy} onClick={openJotform} className="btn-ghost text-xs">
                <ArrowSquareOut size={12} weight="bold" /> Send in edits (form)
              </button>
            ) : editsChannel === "none" ? (
              <button data-testid={`request-additional-edits-${sample.message_id}`}
                onClick={() => setCommentOpen(o => !o)} className="btn-ghost text-xs">
                <PencilSimple size={12} weight="bold" /> Request additional edits (+$100)
              </button>
            ) : (
              <button data-testid={`edit-comment-${sample.message_id}`}
                onClick={() => setCommentOpen(o => !o)} className="btn-ghost text-xs">
                <PencilSimple size={12} weight="bold" /> {commentOpen ? "Hide" : "Send in edits"}
              </button>
            )
          )}
        </div>
      ) : (
        <div className="mt-4">
          <p className="label" style={{ color: "var(--text-dim)" }}>
            {status === "approved" && sample.decided_at && `Approved on ${new Date(sample.decided_at).toLocaleString()}`}
            {status === "declined" && sample.decided_at && `Edits sent on ${new Date(sample.decided_at).toLocaleString()}`}
            {status === "edits_requested" && "Pending update — your team is working on the next version"}
          </p>
          {sample.decision_note && <p className="text-sm mt-1 whitespace-pre-line">{sample.decision_note}</p>}
        </div>
      )}
      {(editsChannel === "comment" || editsChannel === "none") && commentOpen && allowEdits && status === "pending" && (
        <div className="mt-4 space-y-3">
          {editsChannel === "none" && (
            <div className="hair p-3 text-sm"
              data-testid={`request-additional-edits-banner-${sample.message_id}`}
              style={{ background: "rgba(255, 200, 0, 0.08)", borderColor: "var(--hair)" }}>
              <b>Additional edits are billed at $100.</b> The included revision round is
              complete — submitting this request will queue a paid edits round.
            </div>
          )}
          <textarea placeholder={editsChannel === "none"
              ? "Describe the additional edits you'd like (this is a paid request)…"
              : "Type your requested edits or feedback…"} value={editText}
            data-testid={`edit-text-${sample.message_id}`} onChange={(e) => setEditText(e.target.value)}
            rows={4} className="w-full px-4 py-3" />
          <div className="label block">Attach files (optional)
            <FilePicker ref={editFilesRef} testid={`edit-files-${sample.message_id}`} className="mt-1" />
          </div>
          <button data-testid={`edit-submit-${sample.message_id}`} disabled={busy || !editText.trim()}
            onClick={submit} className="btn-primary justify-center h-11 text-sm">
            <PaperPlaneTilt size={12} weight="bold" /> {busy ? "Sending…"
              : editsChannel === "none" ? "Request additional edits ($100)" : "Send in edits"}
          </button>
        </div>
      )}
      {edits.length > 0 && (
        <div className="mt-4 hair p-4">
          <p className="label mb-2">Your edits · {edits.length}</p>
          <div className="space-y-2">
            {edits.map((e) => (
              <div key={e.message_id} className="text-sm border-l-2 pl-3" style={{ borderColor: "var(--hair)" }}>
                <p className="label" style={{ color: "var(--text-dim)" }}>{new Date(e.sent_at).toLocaleString()}</p>
                <p className="whitespace-pre-line mt-1">{e.body}</p>
                {(e.file_ids || []).map(id => files.find(f => f.file_id === id)).filter(Boolean).map(f => (
                  <button key={f.file_id} type="button" onClick={() => dl(f)} className="btn-ghost text-[11px] h-7 mt-1 mr-2"
                    data-testid={`edit-file-${f.file_id}`}><DownloadSimple size={11} weight="bold" /> {f.original_filename}</button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
      {(["declined", "edits_requested"].includes(status) || (sample.notes || []).length > 0) && (
        <TaskNotes taskId={sample.message_id} role="client" anchorId={`sample-${sample.message_id}`}
          basePath={`/me/samples/${sample.message_id}/notes`} initialNotes={sample.notes || []}
          label="Comment on this sample" testid="client-sample-notes"
          uploadPath="/me/files" />
      )}
    </AccordionCard>
  );
}

function ThreadReplyBox({ rootId, onSent }) {
  const [draft, setDraft, { saved, clear }] = useDraft(
    `client.reply.${rootId}`, { body: "" }
  );
  const [busy, setBusy] = useState(false);
  const filesRef = useRef(null);
  const send = async (e) => {
    e.preventDefault();
    if (!draft.body.trim()) return;
    setBusy(true);
    try {
      const file_ids = [];
      for (const f of Array.from(filesRef.current?.files || [])) {
        const fd = new FormData(); fd.append("file", f); fd.append("note", "Message attachment"); fd.append("tag", "message");
        const { data } = await api.post(`/me/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (data?.file_id) file_ids.push(data.file_id);
      }
      await api.post("/me/messages", {
        subject: "Reply", body: draft.body, reply_to_message_id: rootId, file_ids,
      });
      clear();
      if (filesRef.current) filesRef.current.value = "";
      toast.success("Reply sent");
      onSent && onSent();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={send} className="mt-4 space-y-2" data-testid={`thread-reply-${rootId}`}>
      <textarea value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })}
        rows={3} placeholder="Write a reply…" className="w-full px-3 py-2 text-sm"
        data-testid={`thread-reply-body-${rootId}`} />
      <FilePicker ref={filesRef} testid={`thread-reply-files-${rootId}`} label="Attach files" />
      <div className="flex items-center gap-2 flex-wrap">
        <FormatBar value={draft.body} onChange={(v) => setDraft({ ...draft, body: v })}
          target={`thread-reply-body-${rootId}`} testid={`thread-reply-fmt-${rootId}`} />
        <button disabled={busy || !draft.body.trim()}
          data-testid={`thread-reply-send-${rootId}`}
          className="btn-primary h-10 text-sm">
          <PaperPlaneTilt size={12} weight="bold" /> {busy ? "Sending…" : "Send reply"}
        </button>
        {saved && (
          <span className="label text-xs" data-testid={`thread-reply-saved-${rootId}`}
            style={{ color: "var(--text-dim)" }}>Draft saved</span>
        )}
        {draft.body && (
          <button type="button" onClick={clear}
            data-testid={`thread-reply-clear-${rootId}`}
            className="btn-ghost text-xs">Clear draft</button>
        )}
      </div>
    </form>
  );
}

function MessagesPanel({ messages, onReplySent, userId }) {
  const [q, setQ] = useState("");
  const { system: systemMsgs, roots, repliesOf } = splitMessages(messages, q);
  const [view, setView] = useState("convo");
  const [draft, setDraft, { saved: draftSaved, clear: clearDraft }] = useDraft(
    "client.newmessage", { subject: "Message from client", body: "", dueDate: "" }
  );
  const [busy, setBusy] = useState(false);
  const [allOpen, setAllOpen] = useState(null);
  const filesRef = useRef(null);

  const send = async (e) => {
    e.preventDefault();
    if (!draft.body.trim()) return;
    setBusy(true);
    try {
      const files = Array.from(filesRef.current?.files || []);
      const file_ids = [];
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f); fd.append("note", "Message attachment"); fd.append("tag", "message");
        const { data } = await api.post(`/me/files`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (data?.file_id) file_ids.push(data.file_id);
      }
      await api.post("/me/messages", {
        subject: draft.subject, body: draft.body,
        due_date: draft.dueDate || null, file_ids,
      });
      toast.success("Message sent");
      clearDraft();
      if (filesRef.current) filesRef.current.value = "";
      onReplySent && onReplySent();
    } catch (_e) { toast.error("Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="messages-panel">
      <div className="flex items-baseline justify-between gap-3 mb-4 flex-wrap">
        <h2 className="title-xl text-3xl">Messages</h2>
        <div className="flex items-center gap-3">
          {view === "convo" && roots.length > 1 && (
            <ExpandAllToggle testid="messages-expand-all"
              allOpen={allOpen === true}
              onToggle={(next) => setAllOpen(next)} />
          )}
          <p className="label">{view === "convo"
            ? `${roots.length} thread${roots.length === 1 ? "" : "s"}`
            : `${systemMsgs.length} message${systemMsgs.length === 1 ? "" : "s"}`}</p>
        </div>
      </div>
      <div className="panel p-6 mb-6" data-testid="client-reply-composer">
        <div className="flex items-center justify-between mb-3">
          <p className="label">Start a new message</p>
          {draftSaved && <span className="label text-xs"
            data-testid="client-newmsg-draft-saved"
            style={{ color: "var(--text-dim)" }}>Draft saved</span>}
        </div>
        <form onSubmit={send} className="space-y-3">
          <input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
            data-testid="reply-subject" className="w-full px-4 py-3" placeholder="Subject" />
          <textarea required value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })}
            data-testid="reply-body" rows={4} placeholder="Type your message…" className="w-full px-4 py-3" />
          <FormatBar value={draft.body} onChange={(v) => setDraft({ ...draft, body: v })} target="reply-body" testid="reply-fmt" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <p className="label mb-1">Due date (optional)</p>
              <input type="date" value={draft.dueDate} data-testid="reply-due"
                onChange={(e) => setDraft({ ...draft, dueDate: e.target.value })}
                className="w-full px-3 py-2" />
            </div>
            <div>
              <p className="label mb-1">Attachments (optional)</p>
              <FilePicker ref={filesRef} testid="reply-files" />
            </div>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <button data-testid="reply-send-btn" disabled={busy || !draft.body.trim()}
              className="btn-primary justify-center flex-1 h-12">
              <PaperPlaneTilt size={14} weight="bold" /> {busy ? "Sending…" : "Send"}
            </button>
            {(draft.body || draft.subject !== "Message from client" || draft.dueDate) && (
              <button type="button" onClick={clearDraft}
                data-testid="client-newmsg-clear-draft"
                className="btn-ghost text-xs">Clear draft</button>
            )}
          </div>
        </form>
      </div>
      <div className="mb-4">
        <FilterPills testid="client-msg-view" value={view} onChange={setView}
          options={[
            { k: "convo", label: `Conversations (${roots.length})` },
            { k: "system", label: `System messages (${systemMsgs.length})` },
          ]} />
        <div className="mt-3"><MessageSearch value={q} onChange={setQ} testid="client-msg-search" /></div>
      </div>
      {view === "system" ? <SystemMessagesList items={systemMsgs} testid="client-system-messages" />
        : roots.length === 0
        ? <div className="panel p-6 text-center"><p style={{ color: "var(--text-dim)" }}>{q ? "No matching conversations." : "No conversations yet."}</p></div>
        : (
          <div className="space-y-4">
            {roots.map((m) => {
              const replies = repliesOf(m.message_id);
              const handled = m.thread_status === "handled";
              return (
                <div key={m.message_id}>
                <AccordionCard testid={`message-${m.message_id}`} anchorId={`msg-${m.message_id}`}
                  header={
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="sample-badge">{m.direction === "client_to_admin" ? "You" : "PGD"}</span>
                        <p className="title-xl text-lg" style={{ fontWeight: 700 }}>{m.subject}</p>
                        {m.pinned && (
                          <span className="sample-badge" data-testid={`thread-pinned-badge-${m.message_id}`}
                            style={{ background: "#fef3c7", color: "#78350f" }}><PushPin size={10} weight="fill" /> Pinned</span>
                        )}
                        {handled && (
                          <span className="label px-2 py-0.5"
                            data-testid={`thread-closed-${m.message_id}`}
                            style={{ background: "#e5e7eb", color: "#374151", border: "1px solid #d1d5db", fontWeight: 700 }}>
                            Closed
                          </span>
                        )}
                        {m.due_date && (
                          <span className="label px-2 py-0.5"
                            style={{ background: "#fef3c7", color: "#78350f", border: "1px solid #fde68a" }}>
                            Due {new Date(m.due_date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                          </span>
                        )}
                      </div>
                      <p className="label mt-1">
                        {new Date(m.sent_at).toLocaleString()}
                        {replies.length > 0 && ` · ${replies.length} repl${replies.length === 1 ? "y" : "ies"}`}
                        {(m.file_ids || []).length > 0 && ` · ${m.file_ids.length} file${m.file_ids.length === 1 ? "" : "s"}`}
                      </p>
                    </div>
                  }
                  forceOpen={allOpen === null ? undefined : allOpen}>
                  <p className="whitespace-pre-line leading-relaxed" style={{ color: "var(--text-dim)" }}><LinkifiedText text={m.body} /></p>
                  <MessageFiles m={m} />
                  {replies.map(r => (
                    <div key={r.message_id} className="mt-3 pl-4 border-l-2" style={{ borderColor: "var(--hair)" }}>
                      <MessageRow m={r} hideSubject />
                    </div>
                  ))}
                  {handled && (
                    <p className="label mt-4" style={{ color: "var(--text-dim)" }}>
                      This thread was marked handled. Replying will reopen it.
                    </p>
                  )}
                  <ThreadReplyBox rootId={m.message_id} onSent={onReplySent} />
                </AccordionCard>
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}

function MessageRow({ m, hideSubject = false }) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-4">
        <div className="flex items-center gap-2">
          <span className="sample-badge">{m.direction === "client_to_admin" ? "You" : "PGD"}</span>
          {!hideSubject && <p className="title-xl text-lg">{m.subject}</p>}
        </div>
        <p className="label">{new Date(m.sent_at).toLocaleString()}</p>
      </div>
      <p className="mt-2 whitespace-pre-line leading-relaxed" style={{ color: "var(--text-dim)" }}><LinkifiedText text={m.body} /></p>
      <MessageFiles m={m} />
    </div>
  );
}

function ChangePasswordCard() {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (nw.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (nw !== confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/me/change-password", { current_password: cur, new_password: nw });
      toast.success("Password changed");
      setCur(""); setNw(""); setConfirm("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="panel p-6 mt-6" data-testid="change-password-card">
      <p className="label mb-3">Change password</p>
      <form onSubmit={submit} className="space-y-3">
        <PasswordInput
          required placeholder="Current password" value={cur}
          onChange={(e) => setCur(e.target.value)}
          testid="pw-current"
        />
        <PasswordInput
          required placeholder="New password (min 6 chars)" value={nw}
          onChange={(e) => setNw(e.target.value)}
          testid="pw-new"
        />
        <PasswordInput
          required placeholder="Confirm new password" value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          testid="pw-confirm"
        />
        <button data-testid="pw-submit" disabled={busy || !cur || !nw || !confirm}
          className="btn-primary justify-center w-full h-11 text-sm">
          {busy ? "Saving…" : "Update password"}
        </button>
      </form>
    </div>
  );
}

function PreferencesPanel({ prefs, onChange, authProvider }) {
  const items = [
    ["reminders", "Task reminder emails", "Every 2 days for open onboarding steps"],
    ["payment_reminders", "Payment reminder emails", "Before each scheduled payment due date"],
    ["samples", "Sample delivery emails", "When your team sends you a new sample"],
    ["messages", "Message notifications", "When your team sends a direct message"],
  ];
  return (
    <>
      <div className="panel p-6 mt-6" data-testid="preferences-panel">
        <p className="label mb-3">Email notifications</p>
        <div className="space-y-3">
          {items.map(([k, t, sub]) => (
            <label key={k} className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" data-testid={`pref-${k}`}
                checked={prefs?.[k] ?? true}
                onChange={(e) => onChange(k, e.target.checked)}
                className="w-4 h-4 mt-1" />
              <div>
                <p className="font-medium text-sm">{t}</p>
                <p className="label" style={{ color: "var(--text-dim)" }}>{sub}</p>
              </div>
            </label>
          ))}
        </div>
      </div>
      {authProvider === "password" && <ChangePasswordCard />}
    </>
  );
}

function Sidebar({ data, payments }) {
  const nextPay = data.next_payment;
  const eta = data.estimated_completion_date;
  // Parse date-only strings safely to avoid off-by-one from UTC->local conversion
  const fmt = (s) => {
    if (!s) return "—";
    const iso = String(s).slice(0, 10);
    const [y, m, d] = iso.split("-").map(Number);
    if (!y || !m || !d) return s;
    return new Date(y, m - 1, d).toLocaleDateString();
  };
  const overdueDays = (() => {
    if (!nextPay?.due_date) return 0;
    const iso = String(nextPay.due_date).slice(0, 10);
    const [y, m, d] = iso.split("-").map(Number);
    if (!y || !m || !d) return 0;
    const due = new Date(y, m - 1, d);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return Math.round((today - due) / 86400000);
  })();
  return (
    <div className="panel p-5" data-testid="client-sidebar">
      <p className="label mb-3">At a glance</p>
      <div className="space-y-4">
        <div>
          <p className="label" style={{ color: "var(--text-dim)" }}>Est. completion</p>
          <p className="title-xl text-2xl mt-1" data-testid="sidebar-eta">
            {fmt(eta)}
          </p>
        </div>
        <div className="hair" style={{ height: 1 }} />
        <div>
          <p className="label" style={{ color: "var(--text-dim)" }}>Next payment</p>
          {nextPay ? (
            <>
              <p className="title-xl text-2xl mt-1" data-testid="sidebar-next-payment-amount">
                ${Number(nextPay.amount).toFixed(2)}
              </p>
              {overdueDays > 0 ? (
                <p className="label mt-1" data-testid="sidebar-next-payment-overdue"
                  style={{ color: "#991b1b", fontWeight: 700 }}>
                  Overdue by {overdueDays} day{overdueDays === 1 ? "" : "s"} · was due {fmt(nextPay.due_date)}
                </p>
              ) : (
                <p className="label mt-1">Due {fmt(nextPay.due_date)}</p>
              )}
            </>
          ) : <p className="text-sm mt-1" style={{ color: "var(--text-dim)" }}>None scheduled</p>}
        </div>
        <div className="hair" style={{ height: 1 }} />
        <div>
          <p className="label" style={{ color: "var(--text-dim)" }}>Pending items</p>
          <div className="mt-2 space-y-2">
            <div className="flex justify-between text-sm"><span>Tasks complete</span><span className="title-xl">{data.progress.done}</span></div>
            <div className="flex justify-between text-sm"><span>Tasks pending</span><span className="title-xl">{data.progress.pending}</span></div>
            <div className="flex justify-between text-sm"><span>Confirming receipt</span><span className="title-xl">{data.progress.needs_verification}</span></div>
            {typeof data.samples_pending === "number" && data.samples_pending > 0 && (
              <div className="flex justify-between text-sm" data-testid="sidebar-samples-pending">
                <span>Samples to review</span>
                <span className="title-xl">{data.samples_pending}</span>
              </div>
            )}
            {typeof data.marketing_pending === "number" && data.marketing_pending > 0 && (
              <div className="flex justify-between text-sm" data-testid="sidebar-marketing-pending">
                <span>Marketing to review</span>
                <span className="title-xl">{data.marketing_pending}</span>
              </div>
            )}
          </div>
        </div>
        {data.upcoming_appointment && (
          <>
            <div className="hair" style={{ height: 1 }} />
            <div data-testid="sidebar-upcoming-appt">
              <p className="label" style={{ color: "var(--text-dim)" }}>Upcoming appointment</p>
              <p className="title-xl text-xl mt-1">
                {(() => {
                  const dt = new Date(data.upcoming_appointment.datetime);
                  const s = dt.toLocaleString(undefined, {
                    weekday: "short", month: "short", day: "numeric",
                    hour: "numeric", minute: "2-digit",
                    timeZone: "America/New_York",
                  });
                  const tz = new Intl.DateTimeFormat(undefined, {
                    timeZone: "America/New_York", timeZoneName: "short",
                  }).formatToParts(dt).find(p => p.type === "timeZoneName")?.value || "ET";
                  return `${s} · ${tz}`;
                })()}
              </p>
              <p className="label mt-1">{data.upcoming_appointment.type}</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function TechSupportBox() {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    try {
      await api.post("/me/support", { body });
      toast.success("Support request sent");
      setBody(""); setOpen(false);
    } catch (_e) { toast.error("Failed to send"); }
    finally { setBusy(false); }
  };
  return (
    <div className="panel p-6 mt-6" data-testid="tech-support-box">
      <button
        onClick={() => setOpen(o => !o)}
        data-testid="tech-support-toggle"
        className="w-full flex items-center justify-between gap-3"
      >
        <div className="flex items-center gap-3">
          <Lifebuoy size={18} weight="bold" />
          <div className="text-left">
            <p className="title-xl text-lg">Need tech support?</p>
            <p className="label" style={{ color: "var(--text-dim)" }}>Open a quick chat with our team.</p>
          </div>
        </div>
        {open ? <CaretUp size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
      </button>
      {open && (
        <form onSubmit={send} className="mt-4 space-y-3">
          <textarea
            data-testid="tech-support-body"
            value={body} onChange={(e) => setBody(e.target.value)}
            rows={4} required placeholder="Describe the issue you're running into…"
            className="w-full px-4 py-3"
          />
          <button
            data-testid="tech-support-send"
            disabled={busy || !body.trim()}
            className="btn-primary justify-center w-full h-11"
          >
            <PaperPlaneTilt size={12} weight="bold" /> {busy ? "Sending…" : "Send to support"}
          </button>
        </form>
      )}
    </div>
  );
}

function ForceChangePasswordModal({ onSuccess }) {
  const [nw, setNw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [cur, setCur] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (nw.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (nw !== confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/me/change-password", { current_password: cur, new_password: nw });
      toast.success("Password updated");
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4"
         style={{ background: "rgba(0,0,0,0.85)" }} data-testid="force-change-password-modal">
      <div className="panel w-full max-w-md p-8">
        <p className="label">Security</p>
        <h3 className="title-xl text-2xl mt-1">Set a new password</h3>
        <p className="text-sm mt-3" style={{ color: "var(--text-dim)" }}>
          For your security, please replace the temporary password we sent you before continuing.
        </p>
        <form onSubmit={submit} className="space-y-3 mt-6">
          <PasswordInput required placeholder="Current (temporary) password"
            value={cur} onChange={(e) => setCur(e.target.value)} testid="fcp-current" />
          <PasswordInput required placeholder="New password (min 6 chars)"
            value={nw} onChange={(e) => setNw(e.target.value)} testid="fcp-new" />
          <PasswordInput required placeholder="Confirm new password"
            value={confirm} onChange={(e) => setConfirm(e.target.value)} testid="fcp-confirm" />
          <button data-testid="fcp-submit" disabled={busy || !cur || !nw || !confirm}
            className="btn-primary w-full justify-center h-12">
            {busy ? "Saving…" : "Update & continue"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function ClientDashboard() {
  const { user, logout, refresh } = useAuth();
  const { theme, setTheme, toggle } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    // Clients default to light theme on first visit; admin default stays dark
    if (!localStorage.getItem("pgd_theme_client_seen")) {
      setTheme("light");
      localStorage.setItem("pgd_theme_client_seen", "1");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const cached = getCached("client-dashboard");
  const [data, setData] = useState(cached?.data ?? null);
  const [payments, setPayments] = useState(cached?.payments ?? []);
  const [files, setFiles] = useState(cached?.files ?? []);
  const [messages, setMessages] = useState(cached?.messages ?? []);
  const [methods, setMethods] = useState(cached?.methods ?? null);
  const [prefs, setPrefs] = useState(cached?.prefs ?? {});
  const [webDesignEditUrl, setWebDesignEditUrl] = useState(cached?.webDesignEditUrl ?? "https://form.jotform.com/231074696416157");
  const [authProvider, setAuthProvider] = useState(cached?.authProvider ?? "password");
  const [busy, setBusy] = useState(null);
  const [assignedTasks, setAssignedTasks] = useState(cached?.assignedTasks ?? []);
  const [tab, setTab] = useState(() => {
    const p = new URLSearchParams(location.search).get("tab");
    return p || "required";
  });

  // Sync tab -> URL so notification bell "View all" can deep-link and refreshes keep the tab.
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    if (p.get("tab") !== tab) {
      const hadTab = p.has("tab");
      p.set("tab", tab);
      navigate({ search: `?${p.toString()}`, hash: "" }, { replace: !hadTab });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Respond to external navigation changes (e.g. bell -> View all -> ?tab=notifications).
  useEffect(() => {
    const p = new URLSearchParams(location.search).get("tab");
    if (p && p !== tab) setTab(p);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const loadAll = async () => {
    const [ck, py, fl, msg, mth, pr, ps, at] = await Promise.all([
      api.get("/me/checklist"), api.get("/me/payments"),
      api.get("/me/files"), api.get("/me/messages"),
      api.get("/public/payment-methods"), api.get("/me/preferences"),
      api.get("/public/settings"), api.get("/me/admin-tasks"),
    ]);
    setData(ck.data); setPayments(py.data); setFiles(fl.data);
    setMessages(msg.data); setMethods(mth.data);
    const nextPrefs = pr.data.notification_preferences || {};
    setPrefs(nextPrefs);
    const nextAuth = pr.data?.auth_provider || "password";
    if (pr.data?.auth_provider) setAuthProvider(nextAuth);
    const nextWebUrl = ps.data?.web_design_edit_form_url || "https://form.jotform.com/231074696416157";
    if (ps.data?.web_design_edit_form_url) setWebDesignEditUrl(nextWebUrl);
    // Only tasks admin assigned to me (not client-submitted ones — those live in Submit Task tab)
    const nextAssigned = (at.data || []).filter(t => t.submitted_by !== "client");
    setAssignedTasks(nextAssigned);
    setCached("client-dashboard", {
      data: ck.data, payments: py.data, files: fl.data, messages: msg.data,
      methods: mth.data, prefs: nextPrefs, webDesignEditUrl: nextWebUrl,
      authProvider: nextAuth, assignedTasks: nextAssigned,
    });
  };
  useEffect(() => { loadAll(); }, []);

  const verify = async (id) => {
    setBusy(id);
    try {
      const { data: r } = await api.post(`/me/tasks/${id}/verify`);
      toast.success(`Task updated → ${r.task.status.replace("_", " ")}`);
      await loadAll();
    } catch (_e) { toast.error("Verification failed"); }
    finally { setBusy(null); }
  };

  const savePref = async (key, val) => {
    setPrefs(p => ({ ...p, [key]: val }));
    try { await api.patch("/me/preferences", { [key]: val }); toast.success("Preferences saved"); }
    catch (_e) { toast.error("Failed"); }
  };

  const submitEdits = async (messageId, edits, file_ids = []) => {
    await api.post(`/me/samples/${messageId}/edits`, { edits, file_ids });
    await loadAll();
  };

  const decideSample = async (messageId, decision, note) => {
    await api.post(`/me/samples/${messageId}/decision`, { decision, note });
    await loadAll();
  };

  const notifyJotformSent = async (messageId) => {
    await api.post(`/me/samples/${messageId}/mark-jotform-sent`);
    await loadAll();
  };

  if (!data) return <PageLoader label="Loading your tasks…" testid="client-dashboard-loader" />;
  const mustChange = user && user.must_change_password;
  const { tasks, progress, offboarding_enabled, offboarding_tasks, client_type } = data;
  const samples = messages.filter(m => m.sample).length;

  // Build "Required" items from everything client is expected to act on
  const todayIso = new Date().toISOString().slice(0, 10);
  const requiredItems = [];
  for (const t of tasks || []) {
    if (t.status === "pending" && !t.archived) {
      requiredItems.push({ key: `onb-${t.task_id}`, kind: "Onboarding", title: t.title, due_date: t.due_date || null, link: "/dashboard?tab=checklist" });
    }
  }
  for (const t of offboarding_tasks || []) {
    if (t.status === "pending" && !t.archived) {
      requiredItems.push({ key: `off-${t.task_id}`, kind: "Offboarding", title: t.title, due_date: t.due_date || null, link: "/dashboard?tab=offboarding" });
    }
  }
  for (const m of messages || []) {
    if (m.sample && (m.sample_status || "pending") === "pending") {
      const title = m.sample_title || (m.subject || "").replace(/^\[Sample\]\s*/, "") || "Awaiting your decision";
      requiredItems.push({ key: `smp-${m.message_id}`, kind: "Sample", title, due_date: m.due_date || null, link: "/dashboard?tab=samples" });
    }
  }
  for (const p of payments || []) {
    if (p.status !== "paid" && !p.archived && p.due_date && p.due_date <= todayIso) {
      requiredItems.push({ key: `pay-${p.payment_id}`, kind: "Payment", title: `$${Number(p.amount).toFixed(2)} · ${p.note || "Invoice"}`, due_date: p.due_date, link: "/dashboard?tab=payments" });
    }
  }
  if ((data.marketing_pending || 0) > 0) {
    requiredItems.push({ key: "mkt-summary", kind: "Marketing", title: `${data.marketing_pending} marketing update(s) awaiting your approval`, due_date: null, link: "/dashboard?tab=marketing" });
  }
  for (const t of assignedTasks || []) {
    if (t.status !== "complete" && !t.archived) {
      requiredItems.push({ key: `atk-${t.task_id}`, kind: "Admin task", title: t.title, due_date: t.due_date || null, link: "/dashboard?tab=submit-task" });
    }
  }

  const tabs = [
    { key: "required", label: "Pending", count: requiredItems.length },
    { key: "checklist", label: "Tasks", count: tasks.filter(t => !t.archived).length },
    { key: "payments", label: "Payments", count: payments.filter(p => !p.archived).length },
    ...(((client_type || "").toLowerCase() === "marketing")
      ? [{ key: "marketing", label: "Marketing" }] : []),
    { key: "samples", label: "Samples", count: samples },
    { key: "files", label: "Files", count: files.length },
    { key: "messages", label: "Messages", count: (({ roots, system }) => roots.length + system.length)(splitMessages(messages)) },
    { key: "submit-task", label: "Submit Task" },
    ...(offboarding_enabled ? [{ key: "offboarding", label: "Offboarding", count: offboarding_tasks.filter(t => !t.archived).length }] : []),
    { key: "notifications", label: "Notifications" },
    { key: "preferences", label: "Preferences" },
  ];

  return (
    <div className="min-h-screen" data-testid="client-dashboard"
         style={{ background: "var(--bg)", color: "var(--text)" }}>
      {mustChange && <ForceChangePasswordModal onSuccess={refresh} />}
      <header className="border-b" style={{ borderColor: "var(--hair)" }}>
        {/* Row 1: brand + logout */}
        <div className="flex items-center justify-between gap-3 px-6 md:px-6 py-3 md:py-4">
          <Link to="/" className="flex items-center gap-3 min-w-0" data-testid="dash-brand">
            <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center font-semibold"
                 style={{ background: "var(--invert-bg)", color: "var(--invert-text)" }}>P</div>
            <div className="min-w-0">
              <p className="font-medium leading-none truncate">PrettyGrlsDesign</p>
              <p className="label leading-none mt-1">Client Portal</p>
            </div>
          </Link>
          <div className="flex items-center gap-3 min-w-0">
            {user?.email && (
              <span className="label hidden sm:inline truncate"
                style={{ maxWidth: "40vw", color: "var(--text-dim)" }}>
                {user.email}
              </span>
            )}
            <button data-testid="logout-btn" onClick={logout} className="btn-ghost text-xs whitespace-nowrap">
              <SignOut size={14} weight="bold" /> <span className="hidden sm:inline">Log out</span>
            </button>
          </div>
        </div>
        {/* Row 2: quick actions */}
        <div className="flex items-center gap-2 px-6 md:px-6 pb-3 md:pb-4">
          <NotificationBell testid="client-notif-bell" viewAllHref="/notifications" />
          <button onClick={toggle} data-testid="client-theme-toggle" className="btn-ghost p-2" title="Toggle theme">
            {theme === "dark" ? <Sun size={14} weight="bold" /> : <Moon size={14} weight="bold" />}
          </button>
        </div>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-12 gap-0">
        <aside className="md:col-span-5 lg:col-span-4 p-8 md:p-6 border-b md:border-b-0 md:border-r" style={{ borderColor: "var(--hair)" }}>
          <p className="label">Welcome</p>
          <h1 className="title-xl text-5xl md:text-6xl mt-2" data-testid="dash-greeting">
            Hi, {user?.name?.split(" ")[0] || "friend"}.
          </h1>
          <div className="mt-8 flex items-center justify-center panel p-6">
            <ProgressRing pct={progress.pct} done={progress.done} total={progress.total} />
          </div>
          <div className="mt-6 space-y-6">
            {data.project_status_eligible && (
              <ProjectStatusBox
                client={{
                  project_status: data.project_status,
                  pause_reason: data.pause_reason,
                  client_types: data.client_types,
                  client_type: data.client_type,
                }}
                testid="client-project-status" />
            )}
            <Sidebar data={data} payments={payments} />
          </div>
        </aside>

        <main className="md:col-span-7 lg:col-span-8 p-6 md:p-6 space-y-6">
          <Tabs tabs={tabs} active={tab} onChange={setTab} testid="client-tabs" />
          {tab === "required" && (
            <div className="space-y-4" data-testid="client-required-tab">
              <div>
                <h3 className="title-xl text-2xl">Pending · {requiredItems.length}</h3>
                <p className="label mt-1">Everything waiting on you, sorted by soonest due date.</p>
              </div>
              <RequiredActionsPanel items={requiredItems} testid="client-required" />
            </div>
          )}
          {tab === "notifications" && <NotificationsPanel />}
          {tab === "checklist" && (
            <ChecklistSection title="Your tasks" tasks={tasks} onVerify={verify} busy={busy} />
          )}
          {tab === "payments" && <PaymentsPanel payments={payments} methods={methods} />}
          {tab === "marketing" && <MarketingSection userId={user?.user_id} role="client" />}
          {tab === "samples" && <SamplesPanel messages={messages} files={files} clientType={client_type}
                                              webDesignEditUrl={webDesignEditUrl}
                                              onSubmitEdits={submitEdits}
                                              onDecide={decideSample}
                                              onNotifyJotform={notifyJotformSent} />}
          {tab === "files" && <FilesPanel files={files} />}
          {tab === "messages" && <MessagesPanel messages={messages} onReplySent={loadAll} />}
          {tab === "submit-task" && <SubmitTaskPanel />}
          {tab === "preferences" && <PreferencesPanel prefs={prefs} onChange={savePref} authProvider={authProvider} />}
          {tab === "offboarding" && (
            <>
              <ChecklistSection title="Offboarding" tasks={offboarding_tasks} onVerify={verify} busy={busy} />
              <TechSupportBox />
            </>
          )}
        </main>
      </section>
    </div>
  );
}
